import React, { useState } from 'react';

import { DimensionLabelType, Point2D as Point2DInterface } from '@paragon/contract-library';
import { LineSegment2D, Point2D, Vector2D } from '@paragon/geometry-library';
import { notNullish, pairwise } from '@paragon/utilities';

import { useAppSelector } from '../../components/hooks';
import {
  calculateComponentBounds,
  getActiveDimensionBounds,
  getActiveDimensionData,
  getFontSizeFromComponentBoundingBox,
} from '../../store/selectors/boundsSelectors';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { getDistanceFormatterSelector } from '../../store/selectors/getDistanceFormatterSelector';
import { attemptToEliminateOverlap, LabelInfo } from '../adjustForOverlap';
import { getLabelSizeFromTextMesh, LabelSize } from '../util/LabelVisual';
import { calculateLabelPositions, DistanceDimension } from './DistanceDimension';

export function StandardDimensions() {
  const activeComponent = useAppSelector(getActiveComponentDesign)?.component;
  const dimensionData = useAppSelector(getActiveDimensionData);
  const dimensionBounds = useAppSelector(getActiveDimensionBounds);
  const dimensionLabelType = useAppSelector((state) => state.visualOptions.dimensionLabelType);
  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);

  if (!activeComponent || !dimensionData) return null;

  const componentBoundingBox = calculateComponentBounds(activeComponent, false, dimensionBounds);
  const fontSize = getFontSizeFromComponentBoundingBox(componentBoundingBox);

  return dimensionData.map(({ key, dimensionPoints, zeroOnLine }) => (
    <StandardDimensionLine
      key={key}
      dimensionPoints={dimensionPoints}
      zeroOnLine={zeroOnLine}
      dimensionLabelType={dimensionLabelType}
      fontSize={fontSize}
      distanceFormatter={distanceFormatter}
    />
  ));
}

interface LabelSizes {
  dimensionLabelOneSizes: { [dimensionIndex: number]: LabelSize | undefined };
  dimensionLabelTwoSizes: { [dimensionIndex: number]: LabelSize | undefined };
}

const MAX_DIMENSION_LABEL_OFFSET = 12;

interface Props {
  readonly dimensionPoints: readonly Point2DInterface[];
  readonly zeroOnLine: Point2DInterface | undefined;
  readonly dimensionLabelType: DimensionLabelType;
  readonly fontSize: number;
  readonly distanceFormatter: (lengthInInches: number) => string;
}

function StandardDimensionLine({
  dimensionPoints,
  zeroOnLine,
  dimensionLabelType,
  fontSize,
  distanceFormatter,
}: Props) {
  const [labelSizes, setLabelSizes] = useState<LabelSizes>({
    dimensionLabelOneSizes: {},
    dimensionLabelTwoSizes: {},
  });

  if (dimensionPoints.length < 2) return null;

  const showCumulativeLength = dimensionLabelType === 'Cumulative' || dimensionLabelType === 'Combined';
  const showSegmentLength = dimensionLabelType === 'Separated' || dimensionLabelType === 'Combined';

  const isVertical = dimensionPoints[0].x === dimensionPoints[1].x;

  const dimensions = pairwise(dimensionPoints).map((pair, index) => {
    const segment = { start: Point2D.fromObject(pair[0]), end: Point2D.fromObject(pair[1]) };

    const { labelOnePosition, labelTwoPosition } = calculateLabelPositions(segment, { fontSize, antiZoomFactor: 1 });

    const labelOneSize = labelSizes.dimensionLabelOneSizes[index];
    const labelTwoSize = labelSizes.dimensionLabelTwoSizes[index];

    const movementVector = isVertical
      ? new Vector2D(-MAX_DIMENSION_LABEL_OFFSET, 0)
      : new Vector2D(0, MAX_DIMENSION_LABEL_OFFSET);
    const labelOneMovementSegment = LineSegment2D.fromPointObjects(
      labelOnePosition,
      labelOnePosition.translate(movementVector),
    );
    const labelTwoMovementSegment = LineSegment2D.fromPointObjects(
      labelTwoPosition,
      labelTwoPosition.translate(movementVector.reverse()),
    );

    const labelOneInfo: LabelInfo | null = labelOneSize
      ? {
          id: `${index}`,
          center: labelOnePosition,
          size: labelOneSize,
          getMovementSegment: () => labelOneMovementSegment,
        }
      : null;
    const labelTwoInfo: LabelInfo | null = labelTwoSize
      ? {
          id: `${index}`,
          center: labelTwoPosition,
          size: labelTwoSize,
          getMovementSegment: () => labelTwoMovementSegment,
        }
      : null;

    return { dimensionIndex: index, segment, labelOneInfo, labelTwoInfo };
  });

  const labelOneLabelInfoOrNulls = showCumulativeLength ? dimensions.map((dimension) => dimension.labelOneInfo) : [];
  const labelTwoLabelInfoOrNulls = showSegmentLength ? dimensions.map((dimension) => dimension.labelTwoInfo) : [];

  const labelOneLabelInfos = labelOneLabelInfoOrNulls.every(notNullish)
    ? labelOneLabelInfoOrNulls.filter(notNullish)
    : null;
  const labelTwoLabelInfos = labelTwoLabelInfoOrNulls.every(notNullish)
    ? labelTwoLabelInfoOrNulls.filter(notNullish)
    : null;

  const labelOnePositionsAdjustedForOverlap = labelOneLabelInfos ? attemptToEliminateOverlap(labelOneLabelInfos) : null;
  const labelTwoPositionsAdjustedForOverlap = labelTwoLabelInfos ? attemptToEliminateOverlap(labelTwoLabelInfos) : null;

  return (
    <>
      {dimensions.map(({ dimensionIndex, segment }) => {
        const segmentLength = segment.start.distanceTo(segment.end);
        const cumulativeLength =
          zeroOnLine && Math.max(segment.start.distanceTo(zeroOnLine), segment.end.distanceTo(zeroOnLine));

        // Position at origin until labels have been adjusted for overlap.
        const labelOnePosition =
          (labelOnePositionsAdjustedForOverlap ? labelOnePositionsAdjustedForOverlap[`${dimensionIndex}`] : null) ??
          Point2D.origin;
        const labelTwoPosition =
          (labelTwoPositionsAdjustedForOverlap ? labelTwoPositionsAdjustedForOverlap[`${dimensionIndex}`] : null) ??
          Point2D.origin;

        return (
          <DistanceDimension
            key={dimensionIndex}
            segment={segment}
            showEndpointMarks
            distanceFormatter={distanceFormatter}
            labelData={{
              firstNumber: showCumulativeLength ? cumulativeLength : undefined,
              secondNumber: showSegmentLength ? segmentLength : undefined,
              fontSize,
              labelOnePosition,
              labelTwoPosition,
            }}
            label1Callback={(textMesh) => {
              setLabelSizes((labelSizes) => ({
                ...labelSizes,
                dimensionLabelOneSizes: {
                  ...labelSizes.dimensionLabelOneSizes,
                  [dimensionIndex]: getLabelSizeFromTextMesh(textMesh),
                },
              }));
            }}
            label2Callback={(textMesh) => {
              setLabelSizes((labelSizes) => ({
                ...labelSizes,
                dimensionLabelTwoSizes: {
                  ...labelSizes.dimensionLabelTwoSizes,
                  [dimensionIndex]: getLabelSizeFromTextMesh(textMesh),
                },
              }));
            }}
          />
        );
      })}
    </>
  );
}
